import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { WORKSPACE_COLORS } from '@cm/protocol';
import { isHex, normalizeHex } from '../theme';

interface Props {
  /** Cor atual do pane, ou null quando ele herda a do agente. */
  value: string | null;
  /** A cor que o pane teria sem override, para o botao de herdar mostrar. */
  inherited: string;
  /** `null` limpa o override. */
  onPick: (color: string | null) => void;
  /** Fonte fixa deste pane; `undefined` = segue a regra global. */
  fontSize?: number;
  /** `null` devolve o pane a regra global de fonte. */
  onPickFont: (size: number | null) => void;
  onClose: () => void;
  /** Botao que abriu o popover; ancora a posicao. */
  anchor: HTMLElement | null;
}

/**
 * Seletor de cor de um pane.
 *
 * Posicionado com `position: fixed` e coordenadas do `getBoundingClientRect()`
 * do botao -- e nao `absolute` dentro do cabecalho -- porque `.pane` tem
 * `overflow: hidden` para o terminal nao escapar das bordas arredondadas. Um
 * popover `absolute` ali seria cortado no primeiro pixel que passasse do pane.
 */
export function PaneColorPopover({
  value,
  inherited,
  onPick,
  onClose,
  anchor,
  fontSize,
  onPickFont,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [hex, setHex] = useState(value ?? '');
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Mede antes da pintura: posicionar depois faria o popover aparecer no canto
  // e pular para o lugar.
  useLayoutEffect(() => {
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const width = 208;
    // Gruda na borda direita da tela em vez de vazar para fora dela -- o pane
    // mais a direita do mosaico e o caso comum.
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    setPos({ top: r.bottom + 5, left });
  }, [anchor]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const node = ref.current;
      if (!node) return;
      if (node.contains(e.target as Node) || anchor?.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  function commitHex(raw: string) {
    const v = raw.trim();
    if (isHex(v)) onPick(normalizeHex(v));
  }

  return (
    <div
      ref={ref}
      className="pane-popover"
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
      // Sem isto o mousedown sobe ate o `.pane`, que chama focus() e devolve o
      // teclado para o xterm no meio da escolha da cor.
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="section-label" style={{ marginTop: 0 }}>
        Cor deste pane
      </div>

      <div className="color-grid">
        {WORKSPACE_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            className={`color-swatch${value === c ? ' selected' : ''}`}
            style={{ background: c }}
            onClick={() => onPick(c)}
            title={c}
          />
        ))}
      </div>

      <div className="row" style={{ marginTop: 8 }}>
        <input
          type="color"
          value={value ?? inherited}
          onChange={(e) => onPick(e.target.value)}
          title="Escolher uma cor livre"
        />
        <input
          className="hex-input"
          value={hex}
          placeholder={inherited}
          maxLength={7}
          spellCheck={false}
          aria-label="Cor em hexadecimal"
          onChange={(e) => setHex(e.target.value)}
          onBlur={(e) => commitHex(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitHex((e.target as HTMLInputElement).value);
          }}
        />
      </div>

      <button
        type="button"
        className="ghost"
        style={{ marginTop: 6, width: '100%', justifyContent: 'center' }}
        onClick={() => {
          setHex('');
          onPick(null);
        }}
        disabled={value === null}
        title="Volta a usar a cor de identidade do agente"
      >
        herdar a cor do agente
      </button>

      <div className="section-label">Fonte deste pane</div>
      <div className="row">
        <button
          type="button"
          onClick={() => onPickFont(Math.max(6, (fontSize ?? 12) - 1))}
          title="Diminuir"
        >
          A−
        </button>
        <span className="grow" style={{ textAlign: 'center', fontSize: 11 }}>
          {fontSize ? `${fontSize}px` : 'automática'}
        </span>
        <button
          type="button"
          onClick={() => onPickFont(Math.min(22, (fontSize ?? 12) + 1))}
          title="Aumentar"
        >
          A+
        </button>
      </div>
      <button
        type="button"
        className="ghost"
        style={{ marginTop: 4, width: '100%', justifyContent: 'center' }}
        onClick={() => onPickFont(null)}
        disabled={fontSize === undefined}
        title="Volta a seguir o ajuste automático e o zoom global"
      >
        voltar ao automático
      </button>
    </div>
  );
}
